(() => {
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const ready = new WeakSet();
  const revealReady = new WeakSet();
  const reveal = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('wg-shown');
      reveal.unobserve(entry.target);
    });
  }, {threshold: 0.06, rootMargin: '0px 0px 30px 0px'}) : null;
  const lights = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.classList.toggle('wg-lit', entry.isIntersecting));
  }, {rootMargin: '-22% 0px -22% 0px'}) : null;
  function init(scope = document) {
    scope.querySelectorAll('[data-wg-hero]').forEach(hero => {
      if (ready.has(hero)) return;
      ready.add(hero);
      hero.classList.add('wg-ready');
      const life = new AbortController();
      const options = {signal: life.signal};
      const works = [...hero.querySelectorAll('[data-wg-work]')];
      const buttons = [...hero.querySelectorAll('[data-wg-select]')];
      const rails = [...hero.querySelectorAll('[data-wg-rail]')];
      const track = hero.querySelector('[data-wg-track]');
      const stage = hero.querySelector('.wg-stage');
      const toggle = hero.querySelector('[data-wg-toggle]');
      const progressBar = hero.querySelector('[data-wg-progress]');
      const header = document.querySelector('#header-component');
      let selected = '1';
      let selectionRequest = 0;
      let optedOut = Boolean(navigator.connection?.saveData);
      let enabled = false;
      let pinTop = 100;
      let travel = 650;
      let progress = 0;
      let shown = 0;
      let intro = 0;
      let introStart = 0;
      let aimX = 0, aimY = 0, spotX = 0, spotY = 0;
      let frame = 0;
      let resizeFrame = 0;
      const clamp = value => Math.min(1, Math.max(0, value));
      const settle = t => 1 - (1 - t) ** 2.2;
      const ease = t => t * t * (3 - 2 * t);
      // Timeline: title recedes (.03 – .37), view turns to the wall (.08 – .7), backing closes (.1 – .44), glass (.22 – .56), rails join (.34 – .73), frame locks (.68 – .78), light (.74 – .98).
      const paint = () => {
        // The build takes the first 86 % of the scroll, the wall of three the rest.
        const phase = enabled ? clamp(shown / 0.86) : 1;
        hero.style.setProperty('--wg-trio', (enabled ? ease(clamp((shown - 0.88) / 0.12)) : 0).toFixed(4));
        rails.forEach(rail => {
          const t = clamp((phase - 0.34 - Number(rail.dataset.wgRail) * 0.03) / 0.3);
          rail.style.setProperty('--wg-rail-reveal', settle(t).toFixed(4));
        });
        hero.style.setProperty('--wg-e-back', (1 - ease(clamp((phase - 0.1) / 0.34))).toFixed(4));
        hero.style.setProperty('--wg-e-glass', (1 - ease(clamp((phase - 0.22) / 0.34))).toFixed(4));
        const light = clamp((phase - 0.74) / 0.24);
        const focus = settle(clamp((phase - 0.08) / 0.6));
                hero.style.setProperty('--wg-turn', (1 - ease(clamp((phase - 0.08) / 0.62))).toFixed(4));
        hero.style.setProperty('--wg-lock', clamp((phase - 0.68) / 0.1).toFixed(3));
        hero.style.setProperty('--wg-light', light.toFixed(4));
        hero.style.setProperty('--wg-light-opacity', (enabled ? Math.min(1, Math.sin(light * Math.PI) * 1.5) * 0.6 : 0).toFixed(3));
        // Headline: words drift apart (title), the two key words swing out (bell) and join as a small lockup (join).
        const words = enabled ? clamp((phase - 0.03) / 0.34) : 0;
        const join = clamp((words - 0.4) / 0.6);
        hero.style.setProperty('--wg-title', settle(clamp(words / 0.5)).toFixed(3));
        hero.style.setProperty('--wg-bell', Math.sin(Math.PI * words).toFixed(3));
        hero.style.setProperty('--wg-join', (join * join * (3 - 2 * join)).toFixed(3));
        hero.style.setProperty('--wg-dim', focus.toFixed(3));
        // The spot opens once on load (the photograph itself is never hidden), then narrows with the scroll.
        hero.style.setProperty('--wg-spot', ((0.4 + 0.6 * focus) * settle(intro)).toFixed(3));
        hero.style.setProperty('--wg-spot-x', `${(spotX * 46).toFixed(1)}px`);
        hero.style.setProperty('--wg-spot-y', `${(spotY * 30).toFixed(1)}px`);
        hero.classList.toggle('wg-title-gone', enabled && phase > 0.2);
        if (progressBar) progressBar.style.transform = `scaleX(${(enabled ? shown : 1).toFixed(4)})`;
      };
      const update = (now = performance.now()) => {
        frame = 0;
        if (!enabled || !track || document.hidden) return;
        progress = clamp((pinTop - track.getBoundingClientRect().top) / travel);
        // Heavy, eased follow: the frame trails the scroll position slightly instead of jumping with it.
        const gap = progress - shown;
        shown = Math.abs(gap) < 0.0008 ? progress : shown + gap * 0.13;
        introStart ||= now;
        intro = clamp((now - introStart) / 1500);
        spotX += (aimX - spotX) * 0.07;
        spotY += (aimY - spotY) * 0.07;
        paint();
        if (shown !== progress || intro < 1 || Math.abs(aimX - spotX) + Math.abs(aimY - spotY) > 0.002) frame = requestAnimationFrame(update);
      };
      const schedule = () => { if (!frame && enabled) frame = requestAnimationFrame(update); };
      const measure = () => {
        resizeFrame = 0;
        if (!track || !stage) return;
        const headerHeight = header?.getBoundingClientRect().height || 0;
        const stageHeight = stage.getBoundingClientRect().height;
        const mobile = innerWidth < 750;
        const titleHeight = hero.querySelector('h1')?.offsetHeight || 0;
        hero.style.setProperty('--wg-title-h', `${titleHeight + 22}px`);
        pinTop = Math.round(headerHeight + (mobile ? Math.max(titleHeight + 36, (innerHeight - headerHeight - stageHeight) / 2) : 34));
        travel = Math.round(mobile ? Math.min(960, Math.max(600, innerHeight * 1.05)) : Math.min(1750, Math.max(1050, innerHeight * 1.75)));
        hero.style.setProperty('--wg-pin-top', `${pinTop}px`);
        hero.style.setProperty('--wg-stage-height', `${stageHeight}px`);
        hero.style.setProperty('--wg-scroll-travel', `${travel}px`);
        hero.querySelectorAll('.wg-word').forEach(word => word.style.setProperty('--dx', `${(-word.offsetLeft * 0.56).toFixed(1)}px`));
        schedule();
      };
      const scheduleMeasure = () => { if (!resizeFrame) resizeFrame = requestAnimationFrame(measure); };
      const configure = () => {
        enabled = Boolean(rails.length && track && !motion.matches && !optedOut);
        hero.dataset.wgSelected = selected;
        hero.style.setProperty('--wg-slot', String(Number(selected) - 2));
        hero.classList.toggle('wg-scroll-active', enabled);
        if (toggle) {
          toggle.hidden = !rails.length || motion.matches;
          toggle.textContent = enabled ? toggle.dataset.disable : toggle.dataset.enable;
          toggle.setAttribute('aria-pressed', String(enabled));
        }
        if (!enabled) {
          cancelAnimationFrame(frame);
          frame = 0;
          intro = 1;
          spotX = spotY = aimX = aimY = 0;
        }
        paint();
        measure();
      };
      hero.addEventListener('click', async event => {
        if (event.target.closest('[data-wg-toggle]')) {
          optedOut = enabled;
          configure();
          return;
        }
        const button = event.target.closest('[data-wg-select]');
        if (!button || button.getAttribute('aria-pressed') === 'true') return;
        const request = ++selectionRequest;
        const next = works.find(work => work.dataset.wgWork === button.dataset.wgSelect);
        if (!next) return;
        button.setAttribute('aria-busy', 'true');
        const images = [...next.querySelectorAll('img')];
        images.forEach(image => { image.loading = 'eager'; });
        await Promise.allSettled(images.map(image => image.decode()));
        button.removeAttribute('aria-busy');
        if (request !== selectionRequest || life.signal.aborted || !next.querySelector('.wg-build__photo')?.naturalWidth) return;
        const previous = selected;
        selected = button.dataset.wgSelect;
        const fade = !motion.matches;
        works.forEach(work => {
          const key = work.dataset.wgWork;
          work.classList.remove('wg-entering', 'wg-leaving');
          if (key === selected) {
            work.hidden = false;
            if (fade) work.classList.add('wg-entering');
          } else if (key === previous && fade) {
            // Keep the previous photograph underneath until the new one has faded in.
            work.classList.add('wg-leaving');
            setTimeout(() => {
              if (work.dataset.wgWork === selected) return;
              work.hidden = true;
              work.classList.remove('wg-leaving');
            }, 420);
          } else {
            work.hidden = true;
          }
        });
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        configure();
      }, options);
      // The spot leans a little towards the cursor.
      hero.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        const box = hero.getBoundingClientRect();
        aimX = clamp((event.clientX - box.left) / box.width) * 2 - 1;
        aimY = clamp((event.clientY - box.top) / box.height) * 2 - 1;
        schedule();
      }, {...options, passive: true});
      hero.addEventListener('pointerleave', () => { aimX = aimY = 0; schedule(); }, options);
      motion.addEventListener('change', configure, options);
      document.addEventListener('scroll', schedule, {...options, capture: true, passive: true});
      window.addEventListener('resize', scheduleMeasure, options);
      document.addEventListener('visibilitychange', schedule, options);
      const resize = 'ResizeObserver' in window ? new ResizeObserver(scheduleMeasure) : null;
      if (stage) resize?.observe(stage);
      if (header) resize?.observe(header);
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(hero)) return;
        enabled = false;
        resize?.disconnect();
        cancelAnimationFrame(frame);
        cancelAnimationFrame(resizeFrame);
        life.abort();
      }, options);
      configure();
    });
    // Section headings arrive word by word (text nodes only, markup inside stays untouched).
    if (!motion.matches) scope.querySelectorAll('[data-wg-reveal] h2:not([data-wg-split])').forEach(heading => {
      heading.dataset.wgSplit = '';
      let index = 0;
      [...heading.childNodes].filter(node => node.nodeType === 3 && node.textContent.trim()).forEach(node => {
        const parts = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(part => {
          if (!part.trim()) return parts.append(part);
          const word = document.createElement('span');
          word.className = 'wg-w';
          word.style.setProperty('--w', index++);
          word.textContent = part;
          parts.append(word);
        });
        node.replaceWith(parts);
      });
    });
    // Scenes: a section is lit while it crosses the middle of the screen, and dims again when it leaves.
    if (lights) scope.querySelectorAll('.wg-collection,.wg-materials,.wg-rooms,.wg-statement,.wg-product-studio').forEach(scene => {
      if (scene.classList.contains('wg-scene')) return;
      scene.classList.add('wg-scene');
      lights.observe(scene);
    });
    scope.querySelectorAll('[data-wg-reveal]').forEach(element => {
      if (revealReady.has(element)) return;
      revealReady.add(element);
      if (motion.matches || !reveal || element.getBoundingClientRect().top < innerHeight) return;
      element.classList.add('wg-waiting');
      reveal.observe(element);
    });
  }
  // Hand the clicked artwork to the theme's product image transition (see view-transitions.js).
  const artLinks = '.wf-catalog-card__link, .wg-work__image';
  const clearArt = () => document.querySelectorAll('[data-wf-art-transition]').forEach(el => {
    el.removeAttribute('data-wf-art-transition');
    el.removeAttribute('data-view-transition-type');
    el.removeAttribute('data-view-transition-triggered');
  });
  document.addEventListener('click', event => {
    if (motion.matches || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const image = event.target instanceof Element && event.target.closest(artLinks)?.querySelector('img');
    if (!image) return;
    clearArt();
    image.setAttribute('data-wf-art-transition', '');
    image.setAttribute('data-view-transition-type', 'product-image-transition');
    image.setAttribute('data-view-transition-triggered', 'true');
  });
  addEventListener('pageshow', clearArt);
  // Gallery works lean towards the cursor like a picture taken off the wall; the light on them follows.
  const tiltable = '.wg-work__image, .wf-catalog-card__art';
  let tilted = null;
  let tiltFrame = 0;
  const release = () => {
    tilted?.classList.remove('wg-tilting');
    tilted?.style.removeProperty('--tx');
    tilted?.style.removeProperty('--ty');
    tilted = null;
  };
  if (matchMedia('(hover: hover)').matches) document.addEventListener('pointermove', event => {
    if (motion.matches || event.pointerType !== 'mouse' || tiltFrame) return;
    tiltFrame = requestAnimationFrame(() => {
      tiltFrame = 0;
      const target = event.target instanceof Element ? event.target.closest('.wg-work, .wf-catalog-card')?.querySelector(tiltable) : null;
      if (target !== tilted) release();
      if (!target) return;
      const box = target.getBoundingClientRect();
      tilted = target;
      target.classList.add('wg-tilting');
      target.style.setProperty('--tx', (Math.min(1, Math.max(-1, (event.clientX - box.left) / box.width * 2 - 1))).toFixed(3));
      target.style.setProperty('--ty', (Math.min(1, Math.max(-1, (event.clientY - box.top) / box.height * 2 - 1))).toFixed(3));
    });
  }, {passive: true});

  // Small cart preview after "add to cart": what was added, and the way to the cart. Closes itself.
  let preview = null;
  let previewTimer = 0;
  const showCartPreview = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}}).then(response => response.json());
    const item = cart.items?.[0];
    if (!item) return;
    const german = document.documentElement.lang.startsWith('de');
    if (!preview) {
      preview = document.createElement('aside');
      preview.className = 'wf-cart-preview';
      preview.setAttribute('role', 'status');
      preview.innerHTML = '<img alt="" width="72" height="72"><div><p class="wf-cart-preview__label"></p><p class="wf-cart-preview__title"></p><p class="wf-cart-preview__meta"></p></div><a class="wf-cart-preview__link"></a><button type="button" class="wf-cart-preview__close">×</button>';
      preview.querySelector('button').addEventListener('click', () => preview.classList.remove('wf-cart-preview--open'));
      const hold = () => clearTimeout(previewTimer);
      const leave = () => { previewTimer = setTimeout(() => preview.classList.remove('wf-cart-preview--open'), 2500); };
      preview.addEventListener('pointerenter', hold);
      preview.addEventListener('focusin', hold);
      preview.addEventListener('pointerleave', leave);
      preview.addEventListener('focusout', leave);
      document.body.append(preview);
    }
    // Same notation as the shop's own prices (€79,90).
    const amount = (item.final_line_price / 100).toLocaleString(document.documentElement.lang || 'de', {minimumFractionDigits: 2});
    const price = cart.currency === 'EUR' ? `€${amount}` : `${amount} ${cart.currency}`;
    preview.querySelector('img').src = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=160` : '';
    preview.querySelector('.wf-cart-preview__label').textContent = german ? 'Im Warenkorb' : 'Added to cart';
    preview.querySelector('.wf-cart-preview__title').textContent = item.product_title.split(' | ')[0];
    const variant = german ? (item.variant_title || '').replace(/(\d+)x(\d+) cm \/ [^/]*″/, '$1 × $2 cm') : item.variant_title;
    preview.querySelector('.wf-cart-preview__meta').textContent = [variant, item.quantity > 1 ? `${item.quantity} ×` : '', price].filter(Boolean).join(' · ');
    const link = preview.querySelector('a');
    link.href = `${root}cart`;
    link.textContent = german ? `Warenkorb ansehen (${cart.item_count})` : `View cart (${cart.item_count})`;
    preview.querySelector('button').setAttribute('aria-label', german ? 'Schließen' : 'Close');
    preview.classList.remove('wf-cart-preview--open');
    void preview.offsetWidth; // restart the entrance when a second item follows quickly
    preview.classList.add('wf-cart-preview--open');
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => preview.classList.remove('wf-cart-preview--open'), 5500);
  };
  // Room view: show the render of the size that is selected (one Blender render per size, see 03_3D/Raumansicht).
  const showRoomSize = () => {
    const room = document.querySelector('[data-wg-room]');
    const value = document.querySelector('variant-picker input:checked')?.value;
    if (!room || !value) return;
    const key = value.split(' cm')[0].replace(/\s/g, '').replace('×', 'x');
    const url = JSON.parse(room.dataset.wgRoom)[key];
    if (!url || room.dataset.wgRoomKey === key) return;
    room.dataset.wgRoomKey = key;
    room.classList.add('wg-roomview--loading');
    const next = new Image();
    next.onload = next.onerror = () => {
      if (room.dataset.wgRoomKey !== key) return; // a newer choice won
      room.src = url;
      room.classList.remove('wg-roomview--loading');
      const label = document.querySelector('[data-wg-room-size]');
      if (label) label.textContent = key.replace('x', ' × ') + ' cm';
    };
    next.src = url;
  };
  import('@shopify/events').then(({StandardEvents}) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.action !== 'add') return;
      Promise.resolve(event.promise).then(showCartPreview).catch(() => {});
    });
    document.addEventListener(StandardEvents.productSelect, event => {
      Promise.resolve(event.promise).then(showRoomSize).catch(() => {});
    });
  }).catch(() => {});

  // Fallback for browsers without CSS scroll timelines (older Safari, Firefox): drive the small exploded view and the material scene by hand.
  if (!CSS.supports('animation-timeline: view()') && !motion.matches) {
    const unit = value => Math.min(1, Math.max(0, value));
    let pending = 0;
    const drive = () => {
      pending = 0;
      document.querySelectorAll('.wg-mini, .wg-matter').forEach(scene => {
        const box = scene.getBoundingClientRect();
        if (box.bottom < -200 || box.top > innerHeight + 200) return;
        const covered = (innerHeight - box.top) / (innerHeight + box.height); // 0 = enters at the bottom, 1 = has left at the top
        if (scene.classList.contains('wg-mini')) {
          const p = unit(covered / 0.6);
          scene.style.setProperty('--wg-turn', (1 - p).toFixed(3));
          scene.style.setProperty('--wg-e-back', (1 - unit(p / 0.38)).toFixed(3));
          scene.style.setProperty('--wg-e-glass', (1 - unit(p / 0.6)).toFixed(3));
          scene.style.setProperty('--wg-join', unit((p - 0.5) / 0.5).toFixed(3));
          scene.style.setProperty('--wg-lock', unit((p - 0.86) / 0.14).toFixed(3));
        } else {
          scene.classList.add('wg-matter--js');
          scene.style.setProperty('--wg-matter-p', unit((covered - 0.42) / 0.22).toFixed(3));
        }
      });
    };
    const queue = () => { if (!pending) pending = requestAnimationFrame(drive); };
    document.addEventListener('scroll', queue, {capture: true, passive: true});
    addEventListener('resize', queue);
    queue();
  }

  // Escape closes the full-surface menu (the theme itself only closes it via its button).
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelector('.menu-drawer-container[open] > summary')?.click();
  });
  motion.addEventListener('change', () => {
    document.querySelectorAll('.wg-waiting').forEach(el => el.classList.add('wg-shown'));
    document.querySelectorAll('.wg-changing').forEach(el => el.classList.remove('wg-changing'));
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  init();
})();
